import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdCheckCircle, MdEvent, MdHealing, MdPeople } from 'react-icons/md';
import demClinicLogo from 'assets/img/brand/dem-clinic-logo.jpg';
import { bookingsApi } from 'api';
import { UNITIX_SUPPORT_URL } from 'constants';
import { consumeWelcomePending } from 'constants/welcome';
import { BRAND, GRADIENT_NAVY } from 'theme/brandColors';

const FEATURES = [
  { icon: MdEvent, text: 'Расписание и запись пациентов' },
  { icon: MdPeople, text: 'Медкнижки и история визитов' },
  { icon: MdHealing, text: 'Подсказки по симптомам с ИИ' },
];

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function WelcomePortalModal({ userName }) {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [todayCount, setTodayCount] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const bodyText = useColorModeValue('gray.600', 'gray.300');
  const featureBg = useColorModeValue(BRAND.greenMuted, 'whiteAlpha.100');
  const featureIconBg = useColorModeValue('white', 'navy.700');
  const featureText = useColorModeValue(BRAND.navy, 'white');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const footerMuted = useColorModeValue('gray.400', 'gray.500');
  const brandAccent = useColorModeValue(BRAND.navy, 'brand.300');
  const statsBg = useColorModeValue(BRAND.greenMuted, 'whiteAlpha.100');
  const statsBorder = useColorModeValue(BRAND.greenSoft, 'whiteAlpha.200');

  const displayName = userName?.trim() || 'коллега';
  const greetingLine = useMemo(() => `${getTimeGreeting()}, ${displayName}!`, [displayName]);

  useEffect(() => {
    if (consumeWelcomePending()) {
      onOpen();
    }
  }, [onOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingStats(true);
    bookingsApi
      .list({ date: todayIso() })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        const active = list.filter((b) => ['booked', 'confirmed'].includes(b?.status));
        setTodayCount(active.length);
      })
      .catch(() => setTodayCount(null))
      .finally(() => setLoadingStats(false));
  }, [isOpen]);

  const appointmentsLabel = useMemo(() => {
    if (loadingStats) return null;
    if (todayCount === null) return 'Не удалось загрузить расписание на сегодня';
    if (todayCount === 0) return 'На сегодня активных записей нет';
    const n = todayCount;
    const word = n === 1 ? 'запись' : n >= 2 && n <= 4 ? 'записи' : 'записей';
    return `Сегодня у вас ${n} ${word}`;
  }, [todayCount, loadingStats]);

  const handleOpenSchedule = () => {
    onClose();
    navigate('/admin/reception?tab=schedule');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
      <ModalContent
        mx={4}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 24px 64px rgba(30, 58, 95, 0.35)"
      >
        <Box bg={GRADIENT_NAVY} px={{ base: 6, md: 8 }} pt={8} pb={10} position="relative">
          <Box
            position="absolute"
            top="-40px"
            right="-20px"
            w="140px"
            h="140px"
            borderRadius="full"
            bg={BRAND.green}
            opacity={0.15}
            pointerEvents="none"
          />
          <Flex direction="column" align="center" position="relative" zIndex={1}>
            <Flex
              w="72px"
              h="72px"
              borderRadius="18px"
              bg="whiteAlpha.200"
              border="2px solid"
              borderColor="whiteAlpha.400"
              align="center"
              justify="center"
              p={2}
              mb={4}
              boxShadow="0 8px 24px rgba(0,0,0,0.2)"
            >
              <Image src={demClinicLogo} alt="DEM CLINIC" objectFit="contain" w="100%" h="100%" />
            </Flex>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color="whiteAlpha.800"
              mb={2}
            >
              DEM CLINIC · CRM
            </Text>
            <Text
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="800"
              color="white"
              textAlign="center"
              lineHeight="1.25"
            >
              {greetingLine}
            </Text>
            <Box mt={3} minH="24px">
              {loadingStats ? (
                <Skeleton height="18px" width="200px" startColor="whiteAlpha.300" endColor="whiteAlpha.500" />
              ) : (
                <Text fontSize="sm" color="whiteAlpha.900" textAlign="center" fontWeight="600">
                  {appointmentsLabel}
                </Text>
              )}
            </Box>
          </Flex>
        </Box>

        <ModalBody px={{ base: 6, md: 8 }} py={6}>
          <VStack align="stretch" spacing={5}>
            <Text fontSize="md" color={bodyText} lineHeight="1.65" textAlign="center">
              Вы вошли в CRM <Text as="span" fontWeight="700" color={brandAccent}>DEM CLINIC</Text> — расписание,
              пациенты, медкнижки и подсказки по симптомам в одном месте.
            </Text>

            <VStack align="stretch" spacing={2}>
              {FEATURES.map(({ icon: FeatureIcon, text }) => (
                <Flex
                  key={text}
                  align="center"
                  gap={3}
                  px={3}
                  py={2.5}
                  borderRadius="lg"
                  bg={featureBg}
                >
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="md"
                    bg={featureIconBg}
                    align="center"
                    justify="center"
                    color={BRAND.green}
                    flexShrink={0}
                  >
                    <Icon as={FeatureIcon} boxSize={4} />
                  </Flex>
                  <Text fontSize="sm" fontWeight="600" color={featureText}>
                    {text}
                  </Text>
                  <Box ml="auto" color={BRAND.green}>
                    <Icon as={MdCheckCircle} boxSize={4} />
                  </Box>
                </Flex>
              ))}
            </VStack>

            <Button
              size="lg"
              w="full"
              variant="outline"
              colorScheme="brand"
              borderRadius="xl"
              fontWeight="700"
              leftIcon={<Icon as={MdEvent} />}
              onClick={handleOpenSchedule}
            >
              Открыть расписание
            </Button>

            <Button
              size="lg"
              w="full"
              bg={BRAND.green}
              color="white"
              _hover={{ bg: BRAND.greenDark }}
              _active={{ bg: BRAND.greenDark }}
              borderRadius="xl"
              fontWeight="700"
              boxShadow="0 8px 20px rgba(72, 180, 123, 0.35)"
              onClick={onClose}
            >
              Начать работу
            </Button>

            <Text fontSize="xs" color={footerMuted} textAlign="center">
              Разработано в{' '}
              <Link
                href={UNITIX_SUPPORT_URL}
                isExternal
                fontWeight="700"
                color={brandAccent}
                _hover={{ color: BRAND.green, textDecoration: 'underline' }}
              >
                UnitiX
              </Link>
            </Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
